import React, { useCallback, useEffect, useRef, useState } from 'preact/compat';

import CheckIcon from './CheckIcon';
import CopyIcon from './CopyIcon';

interface CopyButtonProps {
  /** The text to copy, or a function returning it lazily. */
  text: string | (() => string);
  /** Accessible label for the button. */
  label?: string;
}

async function writeToClipboard(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // fall through to the legacy approach below
  }

  // Fallback for non-secure contexts where the Clipboard API is unavailable.
  let textarea: HTMLTextAreaElement | undefined;
  try {
    textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    if (textarea?.parentNode) {
      document.body.removeChild(textarea);
    }
  }
}

const CopyButton: React.FC<CopyButtonProps> = ({
  text,
  label = 'Copy message',
}) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const handleCopy = useCallback(async () => {
    const value = typeof text === 'function' ? text() : text;
    if (!value) return;
    const ok = await writeToClipboard(value);
    if (!ok) return;
    setCopied(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), 1500);
  }, [text]);

  return (
    <button
      type='button'
      className={`copy-button ${copied ? 'copied' : ''}`}
      onClick={handleCopy}
      aria-label={copied ? 'Copied' : label}
      title={copied ? 'Copied' : label}
    >
      {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
    </button>
  );
};

export default CopyButton;
